// TopBar — global header bar (assistant presence + engine light + controls).
//
// "Assistant Online / Offline" is REAL (client feedback 2026-07-01 — it used to
// be a hardcoded green dot with a fake cycling "last check-in" counter): it
// derives from the shared ['heartbeats'] cache (same key SystemStatusChip
// actively maintains) — online = any worker daemon beat within the last 5
// minutes; the check-in time is the freshest beat. Next to it sits the
// drafting-engine light (red/green = Claude Code CLI disconnected/connected,
// click → one-click enable + guide). Both are owner-only surfaces (heartbeats
// are owner-readable per migration 0025); other roles just see the clock.

import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import PulseDot from '@/components/shared/PulseDot';
import NotificationsPopover from '@/components/NotificationsPopover';
import { useCommandPalette } from '@/components/CommandPalette';
import { DraftingEngineChip } from '@/components/action-items/DraftingEngineChip';
import { useOwnerProfile } from '@/hooks/useOwnerProfile';
import { supabase } from '@/lib/supabase';
import { readAllHeartbeats, type HeartbeatRow } from '@reverra/shared/heartbeat/read';

const ONLINE_WINDOW_MS = 5 * 60_000;

export function assistantPresence(
  rows: HeartbeatRow[],
  now: Date,
): { online: boolean; minutesAgo: number | null } {
  let latest = 0;
  for (const r of rows) {
    if (!r.payload) continue;
    const t = Date.parse(r.payload.last_seen_at);
    if (Number.isFinite(t) && t > latest) latest = t;
  }
  if (latest === 0) return { online: false, minutesAgo: null };
  const ageMs = Math.max(0, now.getTime() - latest);
  return { online: ageMs < ONLINE_WINDOW_MS, minutesAgo: Math.floor(ageMs / 60_000) };
}

const TopBar = () => {
  const [time, setTime] = useState(new Date());
  const { open: openPalette } = useCommandPalette();
  const { role } = useOwnerProfile();
  const isOwner = role === 'owner';

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Shared with SystemStatusChip / NotificationsPopover — same key, same fn.
  const heartbeatsQ = useQuery({
    queryKey: ['heartbeats'],
    queryFn: () => readAllHeartbeats(supabase),
    refetchInterval: 30_000,
    retry: 0,
    enabled: isOwner,
  });

  const presence = useMemo(
    () => assistantPresence(heartbeatsQ.data ?? [], time),
    [heartbeatsQ.data, time],
  );

  return (
    <header
      className="h-14 flex items-center justify-between px-6 relative z-20"
      style={{ borderBottom: '1px solid var(--border-soft)', background: 'var(--bg-canvas)' }}
    >
      {/* Assistant presence (real heartbeats) + the drafting-engine light.
          Loading/error render neutral — a red "Offline" must only ever come
          from actual heartbeat data, never from a fetch still in flight. */}
      <div className="flex items-center gap-2">
        {isOwner &&
          (heartbeatsQ.isLoading || heartbeatsQ.isError ? (
            <>
              <span className="h-2 w-2 rounded-full bg-muted-foreground/40" aria-hidden="true" />
              <span className="text-xs font-medium text-muted-foreground">
                {heartbeatsQ.isLoading ? 'Assistant · checking…' : 'Assistant · status unavailable'}
              </span>
            </>
          ) : (
            <>
              <PulseDot color={presence.online ? 'green' : 'red'} />
              <span className="text-xs font-medium text-foreground">
                Assistant {presence.online ? 'Online' : 'Offline'}
              </span>
              {presence.minutesAgo !== null && (
                <span className="text-[11px] font-mono text-muted-foreground">
                  · Last check-in {presence.minutesAgo < 1 ? '<1' : presence.minutesAgo}m ago
                </span>
              )}
              <DraftingEngineChip />
            </>
          ))}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={openPalette}
          aria-label="Open command palette"
          title="Search (⌘K)"
          className="neo-chip px-3 py-1.5 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Search className="w-3.5 h-3.5" />
          <span className="hidden sm:inline text-xs">Search</span>
          <kbd className="hidden sm:inline text-[10px] px-1.5 py-0.5 rounded bg-white/70 font-mono">
            ⌘K
          </kbd>
        </button>

        <NotificationsPopover />

        <span className="text-xs font-mono text-muted-foreground">
          {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </header>
  );
};

export default TopBar;
